import React, {useEffect, useRef, useState} from 'react';
import {useSelector} from 'react-redux';

const companyFields = [
    ['id', 'Record ID'], ['userId', 'User ID'], ['name', 'Company name'],
    ['email', 'Email'], ['phone', 'Phone'], ['vatNumber', 'VAT number'],
    ['isClient', 'Client'], ['isDistributor', 'Distributor']
];
const integrationFields = [
    ['agronetId', 'Agronet ID'], ['agronetEmail', 'Agronet email'],
    ['agronetCompanyId', 'Agronet company ID'], ['greencloudId', 'Greencloud ID'],
    ['fakturowniaId', 'Fakturownia ID']
];
const addressFields = [['City', 'City'], ['Street', 'Street'], ['Country', 'Country'],
    ['ZipCode', 'ZIP code'], ['PostalCode', 'Postal code']];

export default function AgronetProCompanyDetails({user, onClose, onAddSubscription, subscriptionRefresh}) {
    const t = useSelector(state => state.language.translator);
    const dialog = useRef(null);
    const [data, setData] = useState(null);
    const [pending, setPending] = useState(true);
    const [error, setError] = useState('');
    const [refresh, setRefresh] = useState(0);

    useEffect(() => {
        const element = dialog.current;
        element.showModal();
        return () => element.close();
    }, []);

    useEffect(() => {
        const controller = new AbortController();
        setPending(true);
        setError('');
        setData(null);
        fetch(`${process.env.REACT_APP_API_URL}/agronetpro_companies/user/${user.id}`, {
            signal: controller.signal,
            headers: {Authorization: sessionStorage.getItem('agronetToken'),
                Local: localStorage.getItem('lang') || 'en'}
        }).then(response => {
            if (response.status === 404) throw new Error(t.gettext('Agronet Pro company data not found'));
            if (response.status === 403) throw new Error(t.gettext('Access denied'));
            if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);
            return response.json();
        }).then(result => {
            if (!controller.signal.aborted) setData(result);
        }).catch(err => {
            if (!controller.signal.aborted) setError(err.message);
        }).finally(() => {
            if (!controller.signal.aborted) setPending(false);
        });
        return () => controller.abort();
    }, [user.id, refresh, t, subscriptionRefresh]);

    const section = (title, fields) => <fieldset>
        <legend>{t.gettext(title)}</legend>
        <div className="companyDetailsFields">{fields.map(([key, label]) => {
            const value = data[key];
            const display = value === null || value === undefined || value === ''
                ? t.gettext('Not provided')
                : typeof value === 'boolean' ? t.gettext(value ? 'Yes' : 'No') : String(value);
            return <label key={key}>{t.gettext(label)}<input readOnly value={display} /></label>;
        })}</div>
    </fieldset>;

    return <dialog ref={dialog} className="agronetProCompanyDetails" aria-labelledby="companyDetailsTitle"
        onCancel={event => {event.preventDefault(); onClose();}}>
        <div className="companyDetailsHeader">
            <h2 id="companyDetailsTitle">{t.gettext('Agronet Pro company details')}</h2>
            <button type="button" onClick={onClose} autoFocus>{t.gettext('Close')}</button>
        </div>
        <p>{user.name} — {user.email}</p>
        {pending && <p role="status">{t.gettext('Loading')}…</p>}
        {error && <div role="alert">{t.gettext('Unable to load company details')}: {error}{' '}
            <button type="button" onClick={() => setRefresh(value => value + 1)}>{t.gettext('Retry')}</button>
        </div>}
        {data && <form onSubmit={event => event.preventDefault()}>
            {section('Company details', companyFields)}
            <fieldset className="latestSubscriptionGroup">
                <legend>{t.gettext('Latest subscription')}</legend>
                <div className="companyDetailsFields">
                    {[['latestSubscriptionDate', 'Subscription date'], ['latestSubscriptionSum', 'Subscription sum']].map(([key, label]) =>
                        <div key={key} className="subscriptionDetail">
                            <span>{t.gettext(label)}</span>
                            {data[key] == null ? <button type="button" className="companyDetailsLink" onClick={onAddSubscription}>
                                {t.gettext('add')}
                            </button> : <input aria-label={t.gettext(label)} readOnly value={data[key]} />}
                        </div>)}
                </div>
            </fieldset>
            {section('Integrations', integrationFields)}
            {['address', 'installationAddress', 'invoiceAddress'].map((prefix, index) =>
                <React.Fragment key={prefix}>{section(['Company address', 'Installation address', 'Invoice address'][index],
                    addressFields.map(([suffix, label]) => [prefix + suffix, label]))}</React.Fragment>)}
        </form>}
    </dialog>;
}
